import { Component, EventEmitter, Input, OnInit, OnDestroy, Output, ViewChild } from '@angular/core';
import { AbstractControl, UntypedFormBuilder } from '@angular/forms';
import { IOrganizationGroup } from '../../interfaces/organization-group.interface';
import { ErrorService, FormBase, MultilingualValidators } from '@portal/shared/ui/form';
import { IEntity, IUser } from '@portal/entity-services/interfaces/src';
import { IQueryParams, PageSize, SortOrder } from '@portal/shared/ui/table/src';
import {
  IFilterToApply,
  ISearchCriteria,
  ISelectInput,
  SearchOperator,
  SearchSelector,
} from '@portal/shared/ui/filter';
import { UserService as SharedUserService } from '@portal/shared/user/src';
import { BaseModalComponent } from '@portal/shared/ui/modal';
import { SearchPayloadService } from '@apps/portal/src/app/modules/transactions/services/search-payload.service';
import { SearchCriteriaService } from '../../../transactions/services/search-criteria.service';
import { Router } from '@angular/router';
import { EntityStatus } from '@portal/entity-services/interfaces';
import { ISearchPayload } from '../../../transactions/interfaces';
import { IGroupViewDetailEdit } from '../../interfaces/group-view-detail-edit.interface';
import { Subject, Subscription } from 'rxjs';
import forEach from 'lodash/forEach';
import isEmpty from 'lodash/isEmpty';
import concat from 'lodash/concat';
import without from 'lodash/without';
import cloneDeep from 'lodash/cloneDeep';
import { OrganisationService } from '@portal/entity-services/organisations/src/lib/services/organisation.service';
import { AcquirerTypes } from '@portal/entity-services/payment-contracts/src/lib/lists/acquirer-type.list';
import { ProcessorTypes } from '@portal/entity-services/payment-contracts/src/lib/components/form/processor-type.list';
import { FeatureToggle } from '@portal/shared/auth/feature-toggle/src';
import { Features } from 'environments/enums/features.enum';

declare const $localize;
@Component({
  selector: 'portal-form-organization-group',
  templateUrl: './form-organization-group.component.html',
  styleUrls: ['./form-organization-group.component.scss'],
  standalone: false,
})
export class FormOrganizationGroupComponent extends FormBase<IOrganizationGroup> implements OnInit, OnDestroy {
  @Input() groupModeEdit: boolean;
  @Input() set groupViewDetailEdit(detail: IGroupViewDetailEdit) {
    this.tempGroupViewDetailEdit = detail;
    if (detail && this.groupModeEdit) {
      this.onSetControlGroupOrganizationValue();
    }
  }
  get groupViewDetailEdit(): IGroupViewDetailEdit {
    return this.tempGroupViewDetailEdit;
  }
  @Input() organizationGroup: IEntity;
  @Input() selectedParentEntity: IEntity;
  @Input() selectedParentEntityIDs: string[] = [];
  @Input() isButtonDisabled = false;
  @Input() isSaving = false;
  @Output() filterUpdated: EventEmitter<Map<string, ISearchCriteria>> = new EventEmitter();
  @Output() selectedOrganizationNumber: EventEmitter<number> = new EventEmitter();
  @Output() closeEditForm: EventEmitter<boolean> = new EventEmitter();
  @Output() entityListGroupOrganisationUpdated: EventEmitter<string[]> = new EventEmitter();
  @ViewChild('cancelModal') cancelModal: BaseModalComponent;

  isInvalid = ErrorService.isFieldInvalid;

  removeEntity$ = new Subject<ISelectInput>();
  selectedStaticFilters: ISelectInput[] = [];
  staticFiltersMap: {
    [key: string]: ISelectInput[];
  } = {};
  searchCriteria: Map<string, ISearchCriteria> = new Map([]);
  initialSearchCriteria: Map<string, ISearchCriteria> = new Map().set(SearchSelector.EntityUid, {
    operator: SearchOperator.In,
    values: new Set(['true']),
    selector: SearchSelector.Organization,
    argument: `${true}`,
  });

  // Multiple organisations
  searchPayload: ISearchPayload;
  initialSearchPayload: ISearchPayload = {
    pageNumber: 1,
    pageSize: PageSize.Size10,
    orderCriteria: SearchSelector.Created,
    direction: SortOrder.Desc,
    criteriaMap: new Map<string, ISearchCriteria>([
      [
        SearchSelector.CreatedFrom,
        {
          argument: SearchPayloadService.getLast7DaysDateFilter().start,
          selector: SearchSelector.CreatedFrom,
          operator: SearchOperator.GreaterThanAndEqualTo,
        },
      ],
      [
        SearchSelector.CreatedTo,
        {
          argument: SearchPayloadService.getLast7DaysDateFilter().end,
          selector: SearchSelector.CreatedTo,
          operator: SearchOperator.LessThanAndEqualTo,
        },
      ],
      [
        SearchSelector.RelatedTransaction,
        {
          argument: null,
          operator: SearchOperator.Equal,
          selector: SearchSelector.RelatedTransaction,
        },
      ],
    ]),
  };
  initialUserSearchPayload: IQueryParams = {
    start: 0,
    limit: PageSize.Size100,
    order: SortOrder.Asc,
    populateEntity: true,
    status: [EntityStatus.Active],
    searchCriteria: new Map<string, ISearchCriteria>(),
  };
  selectedParentEntityChanged = false;
  preSelectedOrganizationNumber: number;
  isParentSelection = false;
  maximumLimitOrgToAdd = 100;
  readonly minimumLimitOrgToAdd = 2;
  isParentEntityToExclude = false;
  messageMaximumLimitOrgReached = $localize`Maximum limit of adding 100 organizations reached`;
  messageMinimumLimitOrgReached = $localize`Please add at least two organization to create a group`;
  messageParentEntityNotIncluded = $localize`The selected parent organization will not be included in the group`;
  messageExpandOrganizationDropDown = $localize`Expand the organization drop-down to select specific organizations for the group. If you wish to include all sub-organizations, assign the parent organization to users from the Users Edit page.`;
  usersDetails: IUser[] = [];
  selectedSubOrganizations: Map<string, ISelectInput> = new Map();
  selectedEntityListGroupOrganisation: string[] = [];
  private tempGroupViewDetailEdit: IGroupViewDetailEdit;

  // Group By dropdown properties
  groupByOptions: ISelectInput[] = [
    { id: 'acquirers', text: $localize`Acquirers` },
    { id: 'processors', text: $localize`Processors` }
  ];
  acquirerOptions: ISelectInput[] = [];
  processorOptions: ISelectInput[] = [];
  private groupBySubscription: Subscription;
  isGroupHierarchyRestrictByActive = false;

  constructor(
    private fb: UntypedFormBuilder,
    private sharedUserService: SharedUserService,
    private router: Router,
    customValidators: MultilingualValidators,
    private organisationService: OrganisationService,
    private featureToggleService: FeatureToggle,
    private acquirerTypes: AcquirerTypes,
    private processorTypes: ProcessorTypes,
  ) {
    super(
      fb.group({
        name: [
          '',
          [
            customValidators.required,
            customValidators.validateCharacters,
            customValidators.maxLength(100, 'Group name'),
          ],
        ],
        groupUid: ['', []],
        parentEntityUid: ['', [customValidators.required]],
        entityUids: [[], [customValidators.required]],
        userIds: [[]],
        groupBy: ['', []],
        selectedItems: ['', []],
      }),
    );
  }

  ngOnInit(): void {
    this.searchPayload = cloneDeep(this.initialSearchPayload);
    this.selectedEntityListGroupOrganisation = cloneDeep(
      this.groupViewDetailEdit?.subOrganizations,
    );
    this.setInitialFilterState();

    // Check if GROUP_HIERARCHY_RESTRICT_BY feature is active
    this.isGroupHierarchyRestrictByActive = this.featureToggleService.isConfigFeatureActive(
      Features.GroupHierarchyRestrictBy
    );

    // Reset selectedItems when groupBy changes
    this.groupBySubscription = this.groupBy.valueChanges.subscribe(() => {
      this.selectedItems.setValue('');
    });
  }

  ngOnDestroy(): void {
    if (this.groupBySubscription) {
      this.groupBySubscription.unsubscribe();
    }
  }

  onSetControlGroupOrganizationValue(): void {
    this.name.setValue(this.groupViewDetailEdit?.groupOrganization?.name);
    this.groupUid.setValue(this.groupViewDetailEdit?.groupOrganization?.entityUid);

    // Check feature flag first before parsing restrictByLabels
    this.isGroupHierarchyRestrictByActive = this.featureToggleService.isConfigFeatureActive(
      Features.GroupHierarchyRestrictBy
    );

    // Parse and set restrictByLabels if they exist
    const restrictByLabels = (this.groupViewDetailEdit?.groupOrganization as any)?.restrictByLabels;
    if (restrictByLabels && Array.isArray(restrictByLabels) && restrictByLabels.length > 0) {
      this.parseRestrictByLabels(restrictByLabels);
    }
  }

  get name(): AbstractControl {
    return this.form.get('name');
  }

  get groupUid(): AbstractControl {
    return this.form.get('groupUid');
  }

  onSetControlParentEntityUidValue(): void {
    this.parentEntityUid.setValue(this.groupViewDetailEdit?.parentOrganization.entityUid);
  }

  get parentEntityUid(): AbstractControl {
    return this.form.get('parentEntityUid');
  }

  get entityUids(): AbstractControl {
    return this.form.get('entityUids');
  }

  get userIds(): AbstractControl {
    return this.form.get('userIds');
  }

  get groupBy(): AbstractControl {
    return this.form.get('groupBy');
  }

  get selectedItems(): AbstractControl {
    return this.form.get('selectedItems');
  }

  get isApplyDisabled(): boolean {
    return (
      (!this.isParentSelection &&
        !this.isParentEntityToExclude &&
        this.preSelectedOrganizationNumber < this.minimumLimitOrgToAdd) ||
      this.isParentSelection ||
      this.isParentEntityToExclude ||
      !this.preSelectedOrganizationNumber
    );
  }

  onSelectedOrganisation(entity: IEntity): void {
    this.selectedParentEntityChanged =
      (this.selectedParentEntity?.entityUid &&
        entity?.entityUid !== this.selectedParentEntity?.entityUid) ||
      false;
    // This kind of change is require due to changes detector not able to track the changes...
    this.selectedParentEntity = null;
    setTimeout(() => {
      this.selectedParentEntity = JSON.parse(JSON.stringify(entity));
      // Fetch processors and acquirers when parent entity is selected
      if (entity && entity.entityUid) {
        this.fetchProcessorsAndAcquirers(entity.entityUid);
      }
    }, 0);
  }

  setSelectedParentEntityIDs(entityIDs: string[]): void {
    this.selectedParentEntityIDs = entityIDs;
  }

  onResetParentEntityChanged(): void {
    this.selectedParentEntityChanged = false;
  }

  // Multiple filters
  onFilterApplied(filter: IFilterToApply): void {
    const values = [...new Set(SearchCriteriaService.removeParenthesesFromIds(filter?.argument))];
    this.checkParentEntityPresent(values);
  }

  onAllPreSelectedEntitiesNumber(preSelectedOrganizationNumber: number): void {
    this.preSelectedOrganizationNumber = preSelectedOrganizationNumber;
    this.selectedOrganizationNumber.emit(preSelectedOrganizationNumber);
  }

  onParentSelection(isParentSelection: boolean): void {
    this.isParentSelection = isParentSelection;
    this.isParentEntityToExclude = false;
  }

  onFilterAppliedSelectedEntities(selectedEntities: Map<string, ISelectInput> = new Map()): void {
    this.selectedSubOrganizations = selectedEntities;
    const organizationIds: string[] = Array.from(this.selectedSubOrganizations.values()).map(
      (organization: ISelectInput) => organization.id,
    );

    this.checkParentEntityPresent(organizationIds);
  }

  onStaticFilterUpdated(updatedFlter: IFilterToApply): void {
    if (updatedFlter) {
      const { toRemove, key, value } = updatedFlter;
      if (toRemove) {
        delete this.staticFiltersMap[key];
      } else {
        this.staticFiltersMap[key] = [];

        forEach(value, ({ id, text }: ISelectInput) => {
          this.staticFiltersMap[key].push({ id, text, key });
        });
      }
    }

    this.selectedStaticFilters = [];
    if (!isEmpty(this.staticFiltersMap)) {
      forEach(this.staticFiltersMap, (inputs: ISelectInput[]) => {
        this.selectedStaticFilters = concat(this.selectedStaticFilters, inputs);
      });
    }
  }

  removeStaticFilter(input: ISelectInput): void {
    this.selectedStaticFilters = without(this.selectedStaticFilters, input);
    this.removeEntity$.next(input);
  }

  setEntityUid(): void {
    if (this.searchCriteria.has(SearchSelector.EntityUid)) {
      this.searchCriteria.delete(SearchSelector.EntityUid);
    }
    const argument = this.sharedUserService.userDetails?.entity?.entityUid;
    this.searchCriteria.set(SearchSelector.EntityUid, {
      argument: argument,
      selector: SearchSelector.Organization,
      operator: SearchOperator.SingleEqual,
    });
  }

  setInitialFilterState(): void {
    this.searchCriteria = cloneDeep(this.initialSearchCriteria);
    this.setEntityUid();
  }

  onCancelModalConfirmed(): void {
    this.closeEditForm.emit();
    this.cancelModal.close();
  }

  onCancel(): void {
    this.cancelModal.open();
  }

  setEntityListGroupOrganisation(entityList: string[]): void {
    this.selectedEntityListGroupOrganisation = entityList;
    this.entityListGroupOrganisationUpdated.emit(this.selectedEntityListGroupOrganisation);
  }

  private checkParentEntityPresent(values: string[]): void {
    if (values.includes(this.selectedParentEntity?.entityUid)) {
      this.isParentEntityToExclude = true;
      this.isParentSelection = false;
    } else {
      this.isParentEntityToExclude = false;
    }
  }

  fetchProcessorsAndAcquirers(entityUid: string): void {
    this.organisationService.getAssignedProcessorsAndAcquirers(entityUid).subscribe(
      (data) => {
        // Prepare acquirer options with localized text
        const acquirerOptionsUnsorted = data.acquirers.map((acquirer) => ({
          id: acquirer,
          text: this.acquirerTypes.keyValue[acquirer] || acquirer.replace(/_/g, ' ')
        }));
        // Sort acquirer options alphabetically by text
        this.acquirerOptions = acquirerOptionsUnsorted.sort((a, b) =>
          a.text.toLowerCase().localeCompare(b.text.toLowerCase())
        );

        // Prepare processor options with localized text
        const processorOptionsUnsorted = data.processors.map((processor) => ({
          id: processor,
          text: this.processorTypes.keyValue[processor] || processor.replace(/_/g, ' ')
        }));
        // Sort processor options alphabetically by text
        this.processorOptions = processorOptionsUnsorted.sort((a, b) =>
          a.text.toLowerCase().localeCompare(b.text.toLowerCase())
        );
      },
      (error) => {
        console.error('Error fetching processors and acquirers:', error);
      }
    );
  }

  getRestrictByLabels(): string[] {
    // Only return restrictByLabels if the feature flag is active
    if (!this.isGroupHierarchyRestrictByActive) {
      return [];
    }

    const groupByValue = this.groupBy.value;
    const selectedItemsValue = this.selectedItems.value;

    if (!groupByValue || !selectedItemsValue) {
      return [];
    }

    const labelPrefix = groupByValue.toLowerCase() === 'acquirers' ? 'acquirer' : 'processor';
    return [`${labelPrefix}:${selectedItemsValue}`];
  }

  parseRestrictByLabels(restrictByLabels: string[]): void {
    if (!restrictByLabels || restrictByLabels.length === 0) {
      return;
    }

    // Only parse restrictByLabels if the feature flag is active
    if (!this.isGroupHierarchyRestrictByActive) {
      return;
    }

    // Parse the first label to determine if it's acquirer or processor
    // Since we only allow single selection, we only care about the first item
    const firstLabel = restrictByLabels[0];
    const [type, value] = firstLabel.split(':');

    if (type.toLowerCase() === 'acquirer') {
      this.groupBy.setValue('acquirers');
      this.selectedItems.setValue(value);

      // Fetch processors and acquirers to populate the dropdown options
      if (this.groupViewDetailEdit?.parentOrganization?.entityUid) {
        this.fetchProcessorsAndAcquirers(this.groupViewDetailEdit.parentOrganization.entityUid);
      }
    } else if (type.toLowerCase() === 'processor') {
      this.groupBy.setValue('processors');
      this.selectedItems.setValue(value);

      // Fetch processors and acquirers to populate the dropdown options
      if (this.groupViewDetailEdit?.parentOrganization?.entityUid) {
        this.fetchProcessorsAndAcquirers(this.groupViewDetailEdit.parentOrganization.entityUid);
      }
    }
  }
}
